import { environment } from "src/environments/environment";

/**
 * Configuración del simulador de API del módulo de desempeño.
 *
 * El mock se controla por flags en `environment`. Se leen con acceso por índice
 * y valores por defecto seguros para que los ~18 environments de marca/test que
 * NO definen estos flags queden con el mock APAGADO automáticamente (solo el
 * `environment.ts` de desarrollo lo activa). Cuando el backend real esté listo,
 * basta poner `MOCK_PERFORMANCE_API: false` y todo apunta a la API real.
 */

/**
 * URL base por defecto del backend de desempeño (Scale). Es el fallback: la base
 * real la envía el backend junto con el token (ver {@link getPerformanceApiBase}).
 */
export const PERFORMANCE_API_BASE: string =
	(environment as any)["PERFORMANCE_API_BASE"] || "https://gestiona.scale.cursalab.co/api/";

/**
 * Key de localStorage donde se cachea la base del backend de desempeño que llega
 * junto con el token (`platform-login-token`). Se limpia en el logout junto al
 * `performance_token`.
 */
export const PERFORMANCE_BASE_URL_STORAGE_KEY = "performance_base_url";

/**
 * Normaliza una base de desempeño para concatenar rutas (`${base}v1/app/...`):
 * quita barras finales y garantiza el segmento `/api/`. Así funciona tanto si el
 * backend envía el host pelado (`https://.../co`) como con `/api` incluido.
 */
function normalizePerformanceApiBase(url: string): string {
	let base = (url || "").trim().replace(/\/+$/, "");
	if (!/\/api$/i.test(base)) base += "/api";
	return base + "/";
}

/**
 * Base efectiva del backend de desempeño: la que envió el backend con el token
 * (cacheada en localStorage) o, si no existe, el fallback {@link PERFORMANCE_API_BASE}.
 * La consumen tanto el servicio de API como el interceptor de token, para que
 * ambos armen/detecten la misma URL.
 */
export function getPerformanceApiBase(): string {
	const stored = typeof localStorage !== "undefined"
		? localStorage.getItem(PERFORMANCE_BASE_URL_STORAGE_KEY)
		: null;
	return normalizePerformanceApiBase(stored || PERFORMANCE_API_BASE);
}

/** true si el interceptor debe responder con data mock en vez de ir al backend real. */
export function isPerformanceMockEnabled(): boolean {
	return (environment as any)["MOCK_PERFORMANCE_API"] === true;
}

/** Latencia simulada (ms) que aplica el interceptor a cada respuesta mock. */
export function getMockDelayMs(): number {
	const delay = (environment as any)["MOCK_API_DELAY_MS"];
	return typeof delay === "number" ? delay : 600;
}
